import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D60fromD1 = () => {
  const title = "Calculation of D60 (Shashtamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D60 chart, or Shashtamsa chart, is an important divisional chart in
        Vedic astrology used to analyze karmic influences, past life effects,
        and subtle spiritual challenges. Below is the step-by-step process to
        calculate the D60 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D60 (Shashtamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Jupiter is located at 27° Sagittarius, this degree will be used for
        the calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Sixty Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into sixty equal parts, each spanning 0.5°.
        To find the Shashtamsa position of a planet, we divide its degree in the
        sign by 0.5.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Shashtamsa \ Position = \frac{Degree \ in \ Sign}{0.5}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{0.5} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 0.5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{0.5} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 0.5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{0.5} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 0.5°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 60 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Shashtamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 0.5°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 0.5°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 0.5°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 0.5°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 0.5°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 0.5°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 0.5°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 0.5°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Jupiter at 27° Sagittarius
      </Typography>

      <Typography variant="body1" paragraph>
        Sagittarius is the D1 sign. Jupiter at 27° falls in the fifty-fourth
        0.5° (between 27° and 27.5°). Therefore, it belongs to the{" "}
        <strong>54th sign</strong> from Sagittarius, which is{" "}
        <strong>Cancer</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D60 (Shashtamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D60
        (Shashtamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_shashtamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Shashtamsa the planet is in based on its degree
    if degree <= 0.5:
        # First 0.5° belongs to the same sign
        shashtamsa_sign = zodiac_signs[sign_index]
    elif 0.5 < degree <= 1:
        # Second 0.5° belongs to the 2nd sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 1 < degree <= 1.5:
        # Third 0.5° belongs to the 3rd sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 1.5 < degree <= 2:
        # Fourth 0.5° belongs to the 4th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 2 < degree <= 2.5:
        # Fifth 0.5° belongs to the 5th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 2.5 < degree <= 3:
        # Sixth 0.5° belongs to the 6th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 3 < degree <= 3.5:
        # Seventh 0.5° belongs to the 7th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 3.5 < degree <= 4:
        # Eighth 0.5° belongs to the 8th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 4 < degree <= 4.5:
        # Ninth 0.5° belongs to the 9th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 4.5 < degree <= 5:
        # Tenth 0.5° belongs to the 10th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    # Continue for all 60 parts...

    return shashtamsa_sign

# Example usage
planet = "Jupiter"
degree = 27
sign = "Sagittarius"

shashtamsa_sign = calculate_shashtamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {shashtamsa_sign} in the D60 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Shashtamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        sixty equal parts and assigns the Shashtamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into sixty parts: the first 0.5°, the
        second 0.5°, and so on. It assigns the Shashtamsa based on the planet's
        degree within the sign:
        <br />
        - First 0.5° belongs to the same sign.
        <br />
        - Second 0.5° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 0.5° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 0.5° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 0.5° belongs to the 5th sign from the D1 sign.
        <br />- Sixth 0.5° belongs to the 6th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D60fromD1;
